import { useId, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Eye, EyeOff, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui';
import { cn } from '@/lib/utils';
import { classifySalt } from './salt';

/** `-webkit-text-security` ist nicht in `CSSProperties` typisiert. */
type MaskableStyle = CSSProperties & { WebkitTextSecurity?: 'disc' | 'none' };

/**
 * Maskierungs-Stil (Spec §Scope 5, harte DoD — cross-browser).
 * - Unmaskiert: kein Sonderstil (normaler Text).
 * - Maskiert + `-webkit-text-security` verfuegbar (Chrome/Safari/Edge, Firefox ≥ 116):
 *   Bullets.
 * - Maskiert ohne Unterstuetzung: Fallback `color: transparent` — der Salt bleibt
 *   verdeckt (Anti-Shoulder-Surfing), statt bei fehlender WebKit-Property offen zu
 *   liegen. Rein clientseitig, kein type=password.
 */
export function computeMaskStyle(
  masked: boolean,
  supportsTextSecurity: boolean,
): MaskableStyle {
  if (!masked) {
    return {};
  }
  return supportsTextSecurity
    ? { WebkitTextSecurity: 'disc' }
    : { color: 'transparent', textShadow: 'none' };
}

/** Einmalige Feature-Erkennung (SSR-/Test-sicher). */
function detectTextSecuritySupport(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('-webkit-text-security', 'disc')
  );
}

/**
 * Payment-Link Link-Tester — Salt-Feld (Spec 501, Phase 4).
 *
 * Das hoechste-Risiko-Element: sein einziges Versprechen ist, dass der Salt die
 * Seite nie verlaesst. Sicherheits-Leitplanken (Spec §Scope 5, harte DoD):
 *  - KEIN `type="password"` → keine Password-Manager-Heuristik/Cloud-Sync. Eigene
 *    Maskierung ueber `-webkit-text-security` + Show-Toggle.
 *  - `autoComplete="off"`, `spellCheck={false}`, KEIN `name`-Attribut (nichts, das
 *    Passwort-Heuristiken triggert).
 *  - Der Salt lebt AUSSCHLIESSLICH im Prop/State des Aufrufers — diese Komponente
 *    haelt nur den `masked`-Umschalter (UI-Boolean), nie eine eigene Salt-Kopie,
 *    und schreibt ihn nirgends (kein console/log, kein Storage).
 *
 * Bewusster Trade-off: Die CSS-Maskierung (`-webkit-text-security`/`transparent`)
 * verbirgt den Klartext nur optisch (Anti-Shoulder-Surfing) — Select+Copy des
 * eigenen Feldes zeigt ihn weiterhin. Das ist gewollt: `type=password` waere das
 * groessere Risiko (Password-Manager-Save + Cloud-Sync des Salts). Der Salt ist das
 * eigene Geheimnis des Nutzers; Schutz gegen Mitleser, nicht gegen sich selbst.
 *
 * Dauerhaft sichtbare Vertrauens-Copy + ASCII-Falle-Banner am Feld.
 */

export interface SaltFieldProps {
  /** Der Salt — gehalten im State des Aufrufers, nie hier. */
  value: string;
  onChange: (value: string) => void;
  /**
   * `true` = der aktuelle Wert stammt aus dem Demo-Testvektor → sichtbar als
   * „Beispieldaten — kein echtes Geheimnis" markieren (Spec 503 §6).
   */
  demoNotice?: boolean;
  /** Optionale zusaetzliche Klassen fuer den Wrapper. */
  className?: string;
}

export function SaltField({
  value,
  onChange,
  demoNotice = false,
  className,
}: SaltFieldProps) {
  const { t } = useTranslation('payment-link');
  const [masked, setMasked] = useState(true);
  const inputId = useId();
  const supportsTextSecurity = useMemo(detectTextSecuritySupport, []);

  // Warnung-vs-Blocker (Spec 503, Handover §4): nur der leere Salt blockiert das
  // Rechnen; Länge ≠ 64 und Nicht-Hex sind Warnungen (der 8-Hex-Demo-Vektor läuft).
  const saltClass = classifySalt(value);

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={inputId}>{t('tester.salt.label')}</Label>

      <div className="relative">
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('tester.salt.placeholder')}
          autoComplete="off"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          data-testid="salt-input"
          // Eigene Maskierung statt type=password (Spec §Scope 5), cross-browser.
          style={computeMaskStyle(masked, supportsTextSecurity)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <button
          type="button"
          onClick={() => setMasked((m) => !m)}
          aria-pressed={!masked}
          aria-label={masked ? t('tester.salt.show') : t('tester.salt.hide')}
          className="absolute inset-y-0 right-0 flex touch-manipulation items-center px-3 text-muted-foreground hover:text-foreground"
        >
          {masked ? (
            <Eye className="h-4 w-4" aria-hidden="true" />
          ) : (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Salt-Zustand: leer = Blocker (kein Rechnen), Länge/Charset = Warnung. */}
      {saltClass.ok ? (
        <p className="text-xs text-emerald-600" data-salt-state="ok">
          {t('tester.salt.hexHintOk')}
        </p>
      ) : saltClass.level === 'block' ? (
        <p className="text-xs text-destructive" data-salt-state="block">
          {t('tester.salt.block.empty')}
        </p>
      ) : (
        <p className="text-xs text-amber-700" data-salt-state="warn">
          {saltClass.reason === 'notHex'
            ? t('tester.salt.warn.notHex')
            : t('tester.salt.warn.wrongLength')}
        </p>
      )}

      {/* Demo-Daten deutlich markieren (Spec 503 §6) — echte Salts sind 64-Hex. */}
      {demoNotice && (
        <p className="text-xs font-medium text-sky-700" data-testid="salt-demo-notice">
          {t('tester.salt.demoNotice')}
        </p>
      )}

      {/* ASCII-Falle-Banner — haeufigster Integrationsfehler (V8). */}
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t('tester.salt.asciiBanner')}</span>
      </div>

      {/* Dauerhaft sichtbare Vertrauens-Copy. */}
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t('tester.salt.trust')}</span>
      </div>
    </div>
  );
}
